import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ActivatedRouteSnapshot,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { AuthService } from '@app/core/services/auth.service';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { AppStore } from '@app/stores/appStore';
import { LoginResponse } from 'api/models';
import { UsersService } from '../../../api/services/users.service';
import { TermsOfServiceNotAcceptedError } from '@app/shared/constants/http-context-tokens';

/**
 * How long a cached session may be trusted before its roles are re-fetched.
 *
 * The session lives in memory only and used to be loaded exactly once, so a
 * role granted or revoked server-side stayed invisible until the next login --
 * the DIGLIEF-9323 incident, where the user copy action was missing until the
 * customer signed in again. Navigation within this window still costs nothing.
 */
export const SESSION_MAX_AGE_MS = 60_000;

@Injectable({
    providedIn: 'root',
})
export class AuthGuard {
    private appStore = inject(AppStore);

    constructor(
        public sessionService: SessionService,
        private router: Router,
        private authService: AuthService,
        private usersService: UsersService,
        private toastr: ToastService,
        private translate: TranslateService
    ) {}

    async canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Promise<boolean> {
        // Check if OAuth token is valid
        if (!this.authService.isAuthenticated()) {
            this.authService.initLogin(window.location.pathname);
            return false;
        }

        // Check if user session already exists and is valid
        const existingSession = this.sessionService.getSession();
        if (existingSession?._key) {
            console.log(
                'AuthGuard: Valid OAuth session exists, checking terms of service'
            );

            // Re-fetch a session older than SESSION_MAX_AGE_MS so role changes
            // reach the running app without a new login (DIGLIEF-9323). A failed
            // refresh keeps the user signed in; only a 401/403 sends them to
            // login.
            const stillAuthenticated = await this.refreshSessionIfStale();
            if (!stillAuthenticated) {
                this.authService.initLogin(state.url);
                return false;
            }

            // Check terms of service for existing session
            if (!this.checkTermsOfService(this.sessionService.getSession())) {
                return false;
            }

            // check if user has registered in addons
            if (!this.sessionService.hasRegisteredInAddons()) {
                this.router.navigate(['/login']);
                return false;
            }

            return true;
        }

        // For OAuth, if no session exists, try to load user data from API
        // This happens after successful OAuth authentication
        try {
            console.log(
                'AuthGuard: OAuth authenticated, loading user data from API...'
            );

            // Load user data
            await this.sessionService.loadUserFromApi();

            console.log('AuthGuard: User data loaded and session updated');

            const userData = this.sessionService.getSession();
            if (!userData?._key) {
                // Request went through but produced no session. Letting this
                // pass hands the page a null session it will dereference.
                this.authService.initLogin(state.url);
                return false;
            }

            // Check terms of service for new user data
            if (!this.checkTermsOfService(userData)) {
                return false;
            }

            // check if user has registered in addons
            if (!this.sessionService.hasRegisteredInAddons()) {
                this.router.navigate(['/login']);
                return false;
            }

            return true;
        } catch (error) {
            if (error instanceof TermsOfServiceNotAcceptedError) {
                // ApiInterceptor already navigated to /settings/tos
                return false;
            }

            console.log(
                'AuthGuard: Failed to load user data, redirecting to login',
                error
            );

            // The organization is not unlocked for c4l-addons. Warn the user
            // before bouncing them back to /login.
            if (this.sessionService.isOrganizationNotAllowedError(error)) {
                this.toastr.warning(
                    this.translate.instant('ADDONS.ORGANIZATION_NOT_REGISTERED')
                );
            }

            // Clear any existing session data
            this.sessionService.deleteSession();

            // Redirect to login page with current route info
            this.router.navigate([RoutingEndpoints.login]);

            return false;
        }
    }

    /**
     * Re-fetches a session that has outlived SESSION_MAX_AGE_MS so role
     * changes take effect without a new login.
     *
     * @returns false only when the server actively rejected the session (401),
     * which means the caller has to send the user through login again. A failed
     * request for any other reason -- offline, timeout, 5xx -- keeps the
     * existing session: a flaky connection must not log anyone out.
     */
    private async refreshSessionIfStale(): Promise<boolean> {
        if (!this.appStore.isUserSessionStale(SESSION_MAX_AGE_MS)) {
            return true;
        }

        try {
            await this.sessionService.loadUserFromApi();
            return this.sessionService.getSession()?._key !== undefined;
        } catch (error) {
            if (
                error instanceof HttpErrorResponse &&
                (error.status === 401 || error.status === 403)
            ) {
                return false;
            }

            return true;
        }
    }

    /**
     * Sends the user to the terms of use page once the acceptance deadline has
     * passed. Anything else -- no session, no terms, deadline still open --
     * lets the navigation through unchanged.
     */
    private checkTermsOfService(session: LoginResponse | null): boolean {
        const termsOfService = session?.termsOfService;

        if (
            termsOfService &&
            !termsOfService.accepted &&
            termsOfService.dateAcceptUntil &&
            termsOfService.dateAcceptUntil < new Date().getTime()
        ) {
            this.router.navigate(['/settings/tos']);
            return false;
        }

        return true;
    }
}
